import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import type { PropsWithChildren, ButtonHTMLAttributes, HTMLAttributes } from "react";
import {
  Minus, XCircle, Download, Trash2, CheckCircle, X, FileText, Info, Calendar, Smile, TrendingUp, Target,
  BarChart3, ArrowUp, ArrowDown, Clock, Layers, ArrowRightCircle,
  Shield, Package, DollarSign, Activity, AlertTriangle, Percent,
  TrendingDown, Wallet
} from "lucide-react";
import { Modal } from 'antd'
import domToPdf from "dom-to-pdf";
import { mixGetV1 } from "services/mixin";
import { useParams, Link } from "react-router-dom";
import { API_URL } from "env";
import './style.scss'
import Sparkline from "./components/Sparkline";
import Badge from "./components/Badge";
import HeaderStat from "./components/HeaderStat";
import RuleSettingsForm from "./components/RuleSettingsForm";
import PreTradePlanForm, { DetailsPlans } from "./components/PreTradePlanForm";
import PreEntryJournalForm from "./components/PreEntryJournalForm";
import fetchRulesFromBackend from "./components/FetchRulesFromBackend";
import parseRulesToSettings from "./components/ParseRulesToSettings";
import saveSettingsToBackend from "./components/SaveSettingsToBackend";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import InfoRow from "./components/InfoRowProps";
import { t, TFunction } from "i18next";

// Types
type WithClassName = { className?: string };
type ApiRuleStatus = 'passed' | 'failed' | 'not_executed';

type RuleCheck = {
  key: string;
  title: string;
  description?: string;
  pass: boolean;
  level?: "info" | "warning" | "error";
  value?: number | string | null;
  limit?: number | string | null;
  notes?: string;
  status?: ApiRuleStatus;
};

export type APIRule = {
  id: number;
  account_number: number;
  name: string;
  description?: string;
  condition: any;
  created_at?: string;
  updated_at?: string;
};

export type RuleSession = { start: string; end: string; label?: string; isActive?: boolean; };

export type RuleSettings = {
  maxRiskPercent: number;
  maxPositions: number;
  maxLotsPerTrade: number;
  maxSLPercent: number;
  maxDailyDDPercent: number;
  minRRAllowed: number;
  allowedSessions: RuleSession[];
  violateOutsideSession: boolean;
  maxSLTPChangePercent: number;
  requireFirstTradeGoal: boolean;
  requireJournalBeforeNewTrade: boolean;
};

type ChecklistDay = {
  date: string;
  trader: string;
  equityOpen?: number;
  equityClose?: number;
  ddPercent?: number;
  journalUrl?: string | null;
  rules: RuleCheck[];
  tradesCount: number;
};

export type PreTradePlan = {
  mood: string;
  plannedTrades: number;
  plannedWindows: { start: string; end: string; label?: string }[];
  expectedHighTime?: string;
  expectedLowTime?: string;
  rrTarget: number;
  notes?: string;
  submittedAt: string;
};

export type TradeJournalEntry = {
  id: string;
  createdAt: string;
  mood?: "Calm" | "Focused" | "Anxious" | "Euphoric" | "Stressed";
  conditions: string[];
  entry: number;
  lots: number;
  sl: number;
  rr: number;
  valuePerPoint: number;
  equityAtEntry?: number;
  riskCash?: number;
  riskPct?: number;
  profitAtTP?: number;
  lossAtSL?: number;
};

// Components
const Button: React.FC<PropsWithChildren<ButtonHTMLAttributes<HTMLButtonElement> & WithClassName>> = ({ className = "", children, ...props }) => (
  <button className={`inline-flex items-center gap-2 px-4 py-3 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 bg-white border border-gray-200 text-gray-800 hover:bg-gray-50 hover:border-gray-300 font-medium ${className}`} {...props}>
    {children}
  </button>
);

const Card: React.FC<PropsWithChildren<HTMLAttributes<HTMLDivElement> & WithClassName>> = ({ className = "", children, ...rest }) => (
  <div {...rest} className={`overview-card bg-white rounded-2xl shadow-sm border border-gray-100 ${className}`}>{children}</div>
);

const CardHeader: React.FC<PropsWithChildren<HTMLAttributes<HTMLDivElement> & WithClassName>> = ({ className = "", children, ...rest }) => (
  <div {...rest} className={`p-3 border-b border-gray-100 ${className}`}>{children}</div>
);

const CardContent: React.FC<PropsWithChildren<HTMLAttributes<HTMLDivElement> & WithClassName>> = ({ className = "", children, ...rest }) => (
  <div {...rest} className={`p-3 ${className}`}>{children}</div>
);

const Dialog: React.FC<{
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-5 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
};

// Constants
const RULE_COLUMNS = (t: TFunction) => [
  { key: "max_risk_percent", label: t('MaxRisk') },
  { key: "no_overtrade", label: t('NoOvertrade') },
  { key: "require_stop_loss", label: t('SLRequired') },
  { key: "max_sl_percent", label: t('MaxSL') },
  { key: "max_dd_percent", label: t('MaxDailyDD') },
  { key: "pre_trade_journal", label: t('PreTradeJournal') },
  { key: "require_rr_declared", label: t('RRDeclared') },
  { key: "session_allowed", label: t('Sessions') },
  { key: "require_first_trade_goal", label: t('FirstTradeGoal') },
  { key: "max_sl_tp_change_percent", label: t('MaxSLTPChange') },
];

const RULE_COLUMNS_KEYS = (t: TFunction) => [
  "max_risk_percent", "no_overtrade", "require_stop_loss",
  "max_sl_percent", "max_dd_percent", "pre_trade_journal", "require_rr_declared",
  "session_allowed", "require_first_trade_goal", "max_sl_tp_change_percent"
];

// Utility functions
const calcStatusPassRate = (rules: RuleCheck[]): { pct: number; passed: number; total: number } => {
  const relevant = rules.filter(r => r.status === "passed" || r.status === "failed");
  const passed = relevant.filter(r => r.status === "passed").length;
  const total = relevant.length;
  const pct = total ? Math.round((passed / total) * 100) : 0;
  return { pct, passed, total };
};

const fmt = (date: Date) => date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
const ymd = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (iso: string, n: number) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return ymd(d);
};
const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

const normalizeApiRuleKey = (name: string): string => {
  const n = name.trim().toLowerCase();
  if (n.includes("pre-trade journal")) return "pre_trade_journal";
  if (n.includes("rr target declared")) return "require_rr_declared";
  if (n.includes("max risk")) return "max_risk_percent";
  if (n.includes("no overtrade")) return "no_overtrade";
  if (n.includes("stop loss required") || n.includes("require stop loss")) return "require_stop_loss";
  if (n.includes("max sl/tp change percent")) return "max_sl_tp_change_percent";
  if (n.includes("max sl/tp change") || n.includes("max sl tp change") || n.includes("sl/tp change")) return "max_sl_tp_change_percent";
  if (/\bmax\s*sl\b/.test(n)) return "max_sl_percent";
  if (n.includes("max daily dd")) return "max_dd_percent";
  if (n.includes("allowed trading sessions") || n.includes("allowed sessions")) return "session_allowed";
  if (n.includes("first trade must have goal")) return "require_first_trade_goal";
  return n.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
};

const buildRuleCheckFromApi = (r: APIRule): RuleCheck => {
  const key = normalizeApiRuleKey(r.name);
  const status = (r.condition?.status ?? "not_executed") as ApiRuleStatus;
  const level: RuleCheck["level"] = status === "failed" ? "error" : status === "passed" ? "info" : "warning";

  const limit: RuleCheck["limit"] = (() => {
    switch (key) {
      case "max_risk_percent": return `${r.condition?.max_risk_percent}%`;
      case "max_sl_percent": return `${r.condition?.max_sl_percent}%`;
      case "max_dd_percent": return `${r.condition?.max_dd_percent}%`;
      case "no_overtrade":
        return `${r.condition?.max_positions} positions${r.condition?.max_lots_per_trade ? `, ${r.condition.max_lots_per_trade} lot/trade` : ""}`;
      case "require_rr_declared": return r.condition?.min_rr_allowed != null ? `${r.condition.min_rr_allowed} RR` : (r.condition?.require_rr_declared ?? null);
      case "require_first_trade_goal": return r.condition?.require_first_trade_goal ?? null;
      case "max_sl_tp_change_percent": return `${r.condition?.max_sl_tp_change_percent}%`;
      case "require_stop_loss": return r.condition?.require_stop_loss ?? null;
      default: return null;
    }
  })();

  const notes = key === "session_allowed"
    ? (r.condition?.allowed_sessions || [])
      .map((s: any) => `${s.label || ""} ${s.start}-${s.end} (${s.tz})`)
      .join("; ")
    : undefined;

  return {
    key,
    title: r.name,
    description: r.description,
    pass: status === "passed",
    level,
    limit,
    notes,
    status,
  };
};

const buildDayFromAPI = (
  dateISO: string,
  trader: string,
  settings: RuleSettings,
  apiRuleByDate: Record<string, Record<string, RuleCheck>>,
  apiRuleDefaults: Record<string, RuleCheck>,
  tradesCountByDate: Record<string, number> = {}
): ChecklistDay => {
  const dayRules = apiRuleByDate[dateISO] || {};

  const rules: RuleCheck[] = RULE_COLUMNS_KEYS(t as TFunction).map(key => {
    const rule = dayRules[key] || apiRuleDefaults[key];

    if (rule) {
      return {
        ...rule,
        key,
        status: rule.status || 'not_executed'
      };
    }

    return {
      key,
      title: t(key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())),
      pass: false,
      status: 'not_executed' as ApiRuleStatus,
      level: 'warning' as const
    };
  });

  // Sử dụng số lượng trades thực tế từ state
  const tradesCount = tradesCountByDate[dateISO] || 0;

  return {
    date: dateISO,
    trader,
    equityOpen: 10000,
    equityClose: 10000,
    ddPercent: 0,
    journalUrl: null,
    rules,
    tradesCount,
  };
};

const loadChecklistRange = async (
  trader: string,
  startISO: string,
  endISO: string,
  settings: RuleSettings,
  apiRuleByDate: Record<string, Record<string, RuleCheck>> = {},
  apiRuleDefaults: Record<string, RuleCheck> = {},
  tradesCountByDate: Record<string, number> = {}
): Promise<ChecklistDay[]> => {
  await delay(150);
  const days: string[] = [];
  let d = new Date(startISO + "T00:00:00Z");
  const end = new Date(endISO + "T00:00:00Z");
  for (; d <= end; d = new Date(d.getTime() + 86400000)) {
    days.push(d.toISOString().slice(0, 10));
  }
  return days.map(iso => buildDayFromAPI(iso, trader, settings, apiRuleByDate, apiRuleDefaults, tradesCountByDate));
};

// API Functions
const saveJournalToBackend = async (accountNumber: number, journal: TradeJournalEntry) => {
  const payload = {
    account_number: accountNumber.toString(),
    condition: {
      mood: journal.mood,
      conditions: journal.conditions,
      entry: journal.entry,
      lots: journal.lots,
      sl: journal.sl,
      rr: journal.rr,
      valuePerPoint: journal.valuePerPoint,
      equityAtEntry: journal.equityAtEntry,
      riskCash: journal.riskCash,
      riskPct: journal.riskPct,
      profitAtTP: journal.profitAtTP,
      lossAtSL: journal.lossAtSL
    },
    type: "pre_entry"
  };
  const response = await fetch(`${API_URL}/api/v1/daily_behavioral_checklist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    throw new Error(`Failed to save journal: ${response.status}`);
  }
};

const deleteJournalFromBackend = async (journalId: string) => {
  const response = await fetch(`${API_URL}/api/v1/daily_behavioral_checklist/${journalId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete journal: ${response.status}`);
  }
};

// Main Component
export default function DailyChecklistHistory() {
  // State
  const [settings, setSettings] = useState<RuleSettings>({
    maxRiskPercent: 5,
    maxPositions: 5,
    maxLotsPerTrade: 1,
    maxSLPercent: 2,
    maxDailyDDPercent: 5,
    minRRAllowed: 1,
    allowedSessions: [
      { start: "05:00", end: "14:00", label: "Sydney Trading Session", isActive: true },
      { start: "07:00", end: "16:00", label: "Tokyo Trading Session", isActive: true },
      { start: "14:00", end: "23:00", label: "London Trading Session", isActive: true },
      { start: "19:00", end: "04:00", label: "New York Trading Session", isActive: true },
    ],
    violateOutsideSession: true,
    maxSLTPChangePercent: 10,
    requireFirstTradeGoal: true,
    requireJournalBeforeNewTrade: true,
  });
  const { t } = useTranslation();
  const [trader] = useState("trader_01");
  const [focusDate, setFocusDate] = useState(ymd(new Date()));
  const [rangeDays, setRangeDays] = useState(7);
  const [list, setList] = useState<ChecklistDay[]>([]);
  const [serverRules, setServerRules] = useState<APIRule[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<Record<string, PreTradePlan | undefined>>({});
  const [preTradePlan, setPreTradePlan] = useState<any[]>([]);
  const [apiRuleDefaults, setApiRuleDefaults] = useState<Record<string, RuleCheck>>({});
  const [apiRuleByDate, setApiRuleByDate] = useState<Record<string, Record<string, RuleCheck>>>({});
  const [fullName, setFullName] = useState<string>(() => {
          const userData = localStorage.getItem('user');
          if (!userData) return 'Unknown';
          try {
              const user = JSON.parse(userData);
              const first = user.first_name ?? '';
              const last = user.last_name ?? '';
              const full = `${last} ${first}`.trim();
              if (!full || full === 'null null') return user.username || 'Unknown';
              return full;
          } catch {
              return 'Unknown';
          }
      });
  const [passRate, setPassRate] = useState<number>(0);
  const [passRate7, setPassRate7] = useState<number[]>([]);
  const [todayTrade, setTodayTrade] = useState<number>(0);
  const [preEntry, setPreEntry] = useState<TradeJournalEntry[]>([]);
  const [latestEquity, setLatestEquity] = useState<number>(10000);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [showPersonalInfo, setShowPersonalInfo] = useState<boolean>(false);
  const [personalNumbers, setPersonalNumbers] = useState({
    personalYearNumber: null,
    personalMonthNumber: null,
    personalDayNumber: null
  });
  const [personalNumberMessage, setPersonalNumberMessage] = useState({
    year: '',
    month: '',
    day: ''
  });
  const [showRuleSetting, setShowRuleSetting] = useState<boolean>(true);
  const [showPreTradePlan, setShowPreTradePlan] = useState<boolean>(true);
  const [showPreEntryJournal, setShowPreEntryJournal] = useState<boolean>(true);
  const [showToast, setShowToast] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState<boolean>(false);
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [tradesCountByDate, setTradesCountByDate] = useState<Record<string, number>>({});
  const [isDown, setIsDown] = useState<boolean>(false);
  const [theme, setTheme] = useState<string>('');

  const printRef = useRef<HTMLDivElement>(null);
  const { id, tradePlatform } = useParams();
  const accountNumber = Number(id);

  // Computed values
  const startISO = useMemo(() => addDays(focusDate, -(rangeDays - 1)), [focusDate, rangeDays]);
  const endISO = useMemo(() => focusDate, [focusDate]);
  const planForFocus = useMemo(() => {
    const result = plans[focusDate];

    return result;
  }, [plans, focusDate]);
  // const user = useMemo(() => ({ name: fullname, level: "Disciplined" }), [fullname]);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const daily = useMemo(() => ({ compliance: 0.72, trades: todayTrade }), [todayTrade]);

  const summary = useMemo(() => {
    if (!list.length) return { passRate: 0, days: 0 };
    const rates = list.map(d => d.rules.filter(r => r.pass).length / d.rules.length);
    const avg = rates.reduce((a, b) => a + b, 0) / rates.length;
    return { passRate: avg, days: list.length };
  }, [list]);

  // API Functions with useCallback
  const fetchApiStatuses = useCallback(async (startDate?: string, endDate?: string) => {
    try {
      const base = `/api/v1/trade-rules?account_number=${id}`;
      const dateFilter = startDate && endDate ? `&date_from=${startDate}&date_to=${endDate}` : '';
      const limitParam = `&limit=0`;

      await getPreTradePlan();

      const notExecRes = await mixGetV1(`${base}&status=not_executed${limitParam}`);
      const notExecArr: APIRule[] = (notExecRes?.data ?? []) as APIRule[];

      const defaults: Record<string, RuleCheck> = {};
      notExecArr.forEach((r) => {
        const rc = buildRuleCheckFromApi({ ...r, condition: { ...r.condition, status: "not_executed" } });
        defaults[rc.key] = rc;
      });

      // Fetch all pages for executed rules
      let executedArr: APIRule[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const executedRes = await mixGetV1(`${base}&status_not=not_executed${dateFilter}&limit=0`);
        const pageData: APIRule[] = (executedRes?.data ?? []) as APIRule[];
        executedArr = [...executedArr, ...pageData];

        hasMore = pageData.length === 100;
        page++;

        if (page > 50) {
          console.warn('Reached maximum page limit (50) for trade rules');
          break;
        }
      }

      const toLocalDay = (timestamp?: string) => {
        if (!timestamp) return "";
        const date = new Date(timestamp);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      };

      const recordsByDateAndRule: Record<string, Record<string, APIRule[]>> = {};

      executedArr.forEach((record) => {
        const day = toLocalDay(record.updated_at || record.created_at);
        if (!day) return;

        const ruleKey = normalizeApiRuleKey(record.name);
        if (!recordsByDateAndRule[day]) recordsByDateAndRule[day] = {};
        if (!recordsByDateAndRule[day][ruleKey]) recordsByDateAndRule[day][ruleKey] = [];
        recordsByDateAndRule[day][ruleKey].push(record);
      });


      const aggregatedByDate: Record<string, Record<string, RuleCheck>> = {};
      Object.entries(recordsByDateAndRule).forEach(([date, rulesForDate]) => {
        aggregatedByDate[date] = {};
        Object.entries(rulesForDate).forEach(([ruleKey, records]) => {
          const passedCount = records.filter(r => (r.condition?.status ?? "not_executed") === "passed").length;
          const failedCount = records.filter(r => (r.condition?.status ?? "not_executed") === "failed").length;

          if (passedCount + failedCount === 0) return;

          const finalStatus: ApiRuleStatus = passedCount >= failedCount ? "passed" : "failed";

          const latestRecord = records.sort((a, b) => {
            const timeA = new Date(a.updated_at || a.created_at || "").getTime();
            const timeB = new Date(b.updated_at || b.created_at || "").getTime();
            return timeB - timeA;
          })[0];

          const mergedRecord: APIRule = {
            ...latestRecord,
            condition: { ...latestRecord.condition, status: finalStatus }
          };

          const ruleCheck = buildRuleCheckFromApi(mergedRecord);
          const countNote = `✓ ${passedCount} • ✗ ${failedCount}`;
          aggregatedByDate[date][ruleKey] = {
            ...ruleCheck,
            notes: ruleCheck.notes ? `${ruleCheck.notes} | ${countNote}` : countNote
          };
        });
      });


      // Compute trades count as number of executed (passed/failed) records for Pre-Trade Journal per day
      const preTradeJournalCounts: Record<string, number> = {};
      Object.entries(recordsByDateAndRule).forEach(([date, rulesForDate]) => {
        const journalRecords: APIRule[] = rulesForDate["pre_trade_journal"] || [];
        const executedCount = journalRecords.filter(r => {
          const st = (r.condition?.status ?? "not_executed") as ApiRuleStatus;
          return st === "passed" || st === "failed";
        }).length;
        preTradeJournalCounts[date] = executedCount;
      });

      setApiRuleDefaults(defaults);
      setApiRuleByDate(aggregatedByDate);
      setTradesCountByDate(preTradeJournalCounts);
      const todayStrLocal = new Date().toISOString().split('T')[0];
      setTodayTrade(preTradeJournalCounts[todayStrLocal] || 0);
    } catch (err) {
      console.error("fetchApiStatuses error:", err);
    }
  }, [id]);

  const getPreTradePlan = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/daily_behavioral_checklist?account_number=${id}&type=daily&limit=0&page=1`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const responseData = await response.json();
      const preTradePlanData = responseData?.data?.data || [];
      setPreTradePlan(preTradePlanData);
    } catch (error) {
      console.error('Error fetching pre-trade plan:', error);
      setPreTradePlan([]);
    }
  }, [id]);

  const getLatestEquity = useCallback(async () => {
    try {
      // First try to get equity from live-trading
      const liveTradingResponse = await fetch(`${API_URL}/api/v1/live-trading?account_number=${id}&limit=1`);
      if (liveTradingResponse.ok) {
        const liveData = await liveTradingResponse.json();
        if (liveData.status && liveData.data?.data?.length > 0) {
          const latestLiveRecord = liveData.data.data[0];
          setLatestEquity(Number(latestLiveRecord.equity) || 10000);
          return;
        }
      }

      // If no live-trading data, get from trade-history
      const tradeHistoryResponse = await fetch(`${API_URL}/api/v1/trade-history?account_number=${id}&limit=1`);
      if (tradeHistoryResponse.ok) {
        const tradeData = await tradeHistoryResponse.json();
        if (tradeData.status && tradeData.data?.data?.length > 0) {
          const latestTradeRecord = tradeData.data.data[0];
          setLatestEquity(Number(latestTradeRecord.balance_after) || 10000);
          return;
        }
      }

      // Fallback to default
      setLatestEquity(10000);
    } catch (error) {
      console.error('Error fetching latest equity:', error);
      setLatestEquity(10000);
    }
  }, [id]);

  const getPreEntryData = useCallback(async () => {
    try {
      const responseData = await mixGetV1(`/api/v1/daily_behavioral_checklist?account_number=${id}&type=pre_entry&limit=0&page=1`);
      const data = responseData?.data?.data || [];

      const mappedEntries = data.map((item: any) => ({
        id: item.id,
        createdAt: item.created_at || item.createdAt,
        mood: item.condition.mood,
        conditions: item.condition.conditions,
        entry: Number(item.condition.entry),
        lots: Number(item.condition.lots),
        sl: Number(item.condition.sl),
        rr: Number(item.condition.rr),
        valuePerPoint: Number(item.condition.valuePerPoint),
        equityAtEntry: Number(item.condition.equityAtEntry) || undefined,
        riskCash: Number(item.condition.riskCash) || undefined,
        riskPct: Number(item.condition.riskPct) || undefined,
        profitAtTP: Number(item.condition.profitAtTP) || undefined,
        lossAtSL: Number(item.condition.lossAtSL) || undefined,
      }));
      setPreEntry(mappedEntries);
    } catch (error) {
      console.error('Error fetching pre-entry data:', error);
      setPreEntry([]);
    }
  }, [id]);

  const getTradeRules = useCallback(async () => {
    try {
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);

      // const userData = localStorage.getItem('user');
      // if (userData) {
      //   const userObject = JSON.parse(userData);
      //   setFullName(userObject.fullname);
      // }

      const response = await fetch(`${API_URL}/api/v1/trade-rules?account_number=${accountNumber}&date_from=${sevenDaysAgo.toISOString().split('T')[0]}&date_to=${today.toISOString().split('T')[0]}`);
      const data = await response.json();

      let tradeRulesData: any[] = Array.isArray(data) ? data : (data?.data || []);

      const todayStr = today.toISOString().split('T')[0];
      const todayTrades = tradeRulesData.filter((rule: any) => {
        const updatedAtDate = new Date(rule.updated_at).toISOString().split('T')[0];
        return updatedAtDate === todayStr && ['passed', 'failed'].includes(rule.condition.status);
      });

      const dailyStats: { [date: string]: { passed: number; failed: number } } = {};
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        dailyStats[dateStr] = { passed: 0, failed: 0 };
      }

      tradeRulesData.forEach((rule: any) => {
        const updatedAtDate = new Date(rule.updated_at).toISOString().split('T')[0];
        if (dailyStats[updatedAtDate]) {
          if (rule.condition.status === 'passed') {
            dailyStats[updatedAtDate].passed += 1;
          } else if (rule.condition.status === 'failed') {
            dailyStats[updatedAtDate].failed += 1;
          }
        }
      });

      let totalPassed = 0;
      let totalTrades = 0;
      Object.values(dailyStats).forEach(({ passed, failed }) => {
        totalPassed += passed;
        totalTrades += passed + failed;
      });

      const avgPassRate = totalTrades > 0 ? (totalPassed / totalTrades) * 100 : 0;
      setPassRate(Number(avgPassRate.toFixed(2)));
      setTodayTrade(todayTrades.length);

      const passRate7Data = Object.keys(dailyStats)
        .sort()
        .map((date) => {
          const { passed, failed } = dailyStats[date];
          const total = passed + failed;
          return total > 0 ? Number(((passed / total) * 100).toFixed(2)) : 0;
        });
      setPassRate7(passRate7Data);
    } catch (error) {
      console.error('Error fetching trade rules:', error);
    }
  }, [accountNumber]);

  const fetchPersonalNumbers = useCallback(async () => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return;

      const userObject = JSON.parse(userData);
      const dob = userObject.birthday;
      if (!dob) return;

      let formattedDob: string;
      const date = new Date(dob);
      if (isNaN(date.getTime())) {
        formattedDob = dob;
      } else {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        formattedDob = `${day}/${month}/${year}`;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found in localStorage');
        return;
      }
      const language = localStorage.getItem('i18nextLng') || 'en';
      const response = await fetch(`${API_URL}/api/v1/be/coaching-daily-personal?birthday=${formattedDob}&locale=${language}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      setPersonalNumbers({
        personalYearNumber: data.data.personal_year.number,
        personalMonthNumber: data.data.personal_month.number,
        personalDayNumber: data.data.personal_day.number
      });
      setPersonalNumberMessage({
        year: data.data.personal_year.items[0].description,
        month: data.data.personal_month.items[0].description,
        day: data.data.personal_day.items[0].description
      });
    } catch (error) {
      console.error('Error fetching personal numbers:', error);
    }
  }, []);

  const handleLoadRules = useCallback(async () => {
    try {
      const rules = await fetchRulesFromBackend(accountNumber);
      setServerRules(rules);
      setSettings(prev => parseRulesToSettings(rules, prev));
    } catch (e) {
      console.warn(e);
    }
  }, [accountNumber]);

  const handleSaveSettings = useCallback(async () => {
    try {
      await saveSettingsToBackend(settings, serverRules);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (e) {
      console.warn(e);
    }
  }, [settings, serverRules]);

  const saveJournalEntry = useCallback(async (e: TradeJournalEntry) => {
    try {
      await saveJournalToBackend(accountNumber, e);
      await getPreEntryData();
      await fetchApiStatuses(startISO, endISO);
    } catch (error) {
      console.error('Error saving journal entry:', error);
    }
  }, [accountNumber, getPreEntryData, fetchApiStatuses, startISO, endISO]);

  const removeJournalEntry = useCallback(async (id: string) => {
    try {
      setPreEntry((prev) => prev.filter((entry) => entry.id !== id));
      await deleteJournalFromBackend(id);
      await getPreEntryData();
    } catch (error) {
      console.error('Error removing journal entry:', error);
    }
  }, [getPreEntryData]);

  const handleDownloadPdf = useCallback(() => {
    setIsDown(true);
    const element = printRef.current;
    const options = { filename: "checklist-history.pdf", overrideWidth: 1000, overrideHeight: 800, setDpi: 300 };
    if (!element) return;
    domToPdf(element, options, () => setIsDown(false));
  }, []);

  const formatTo12Hour = useCallback((timeString: string): string => {
    return dayjs(timeString).format("h:mm:ss A");
  }, []);

  // Effects
  useEffect(() => {
    fetchApiStatuses(startISO, endISO);
    getPreEntryData();
    getTradeRules();
    fetchPersonalNumbers();
    getLatestEquity();
  }, [fetchApiStatuses, getPreEntryData, getTradeRules, fetchPersonalNumbers, getLatestEquity, startISO, endISO]);

  useEffect(() => {
    handleLoadRules();
  }, [handleLoadRules]);

  useEffect(() => {
    setLoading(true);
    loadChecklistRange(trader, startISO, endISO, settings, apiRuleByDate, apiRuleDefaults, tradesCountByDate)
      .then((result) => {
        setList(result); // Gán dữ liệu cho state
        console.log(result); // Log dữ liệu trả về từ loadChecklistRange
      })
      .finally(() => setLoading(false));
  }, [trader, startISO, endISO, apiRuleByDate, apiRuleDefaults, tradesCountByDate]);

  // Computed data
  const listWithApiStatus: ChecklistDay[] = useMemo(() => {
    return list.map((d) => {
      const normalizeLimit = (r: RuleCheck): RuleCheck => {
        const parseNum = (v: any) => (typeof v === "string" ? parseFloat(v.replace("%", "")) : Number(v));
        if (r.key === "max_risk_percent") {
          const limit = getLimitNum("max_risk_percent");
          const v = parseNum(r.value);
          const pass = Number.isFinite(v) ? v <= limit : r.pass;
          return { ...r, key: "max_risk_percent", title: `${t('MaxRisk')} ${limit}%`, limit: `${limit}%`, pass };
        }
        if (r.key === "max_risk_percent") {
          const limit = getLimitNum("max_risk_percent");
          const v = parseNum(r.value);
          const pass = Number.isFinite(v) ? v <= limit : r.pass;
          return { ...r, key: "max_risk_percent", title: `${t('MaxRisk')} ${limit}%`, limit: `${limit}%`, pass };
        }
        if (r.key === "max_sl_percent") {
          const limit = getLimitNum("max_sl_percent");
          const v = parseNum(r.value);
          const pass = Number.isFinite(v) ? v <= limit : r.pass;
          return { ...r, key: "max_sl_percent", title: `${t('MaxSL')} ${limit}%`, limit: `${limit}%`, pass };
        }
        if (r.key === "max_sl_percent") {
          const limit = getLimitNum("max_sl_percent");
          const v = parseNum(r.value);
          const pass = Number.isFinite(v) ? v <= limit : r.pass;
          return { ...r, key: "max_sl_percent", title: `${t('MaxSL')} ${limit}%`, limit: `${limit}%`, pass };
        }
        if (r.key === "max_dd_percent") {
          const limit = getLimitNum("max_dd_percent");
          const v = parseNum(r.value);
          const pass = Number.isFinite(v) ? v <= limit : r.pass;
          return { ...r, title: `${t('MaxDailyDD')} ${limit}%`, limit: `${limit}%`, pass };
        }
        if (r.key === "max_dd_percent") {
          const limit = getLimitNum("max_dd_percent");
          const v = parseNum(r.value);
          const pass = Number.isFinite(v) ? v <= limit : r.pass;
          return { ...r, title: `${t('MaxDailyDD')} ${limit}%`, limit: `${limit}%`, pass };
        }
        return r;
      };

      const getLimitNum = (key: string) => {
        const meta = apiRuleByDate[d.date]?.[key] || apiRuleDefaults[key];
        if (!meta?.limit) return ({
          max_risk_percent: 5,
          max_sl_percent: 2,
          max_dd_percent: 5,
        } as any)[key] ?? null;
        if (typeof meta.limit === "string" && meta.limit.endsWith("%")) return Number(meta.limit.slice(0, -1));
        return Number(meta.limit);
      };

      let rules = d.rules.map(normalizeLimit);

      Object.keys(apiRuleDefaults).forEach((key) => {
        if (!rules.some((r) => r.key === key)) {
          rules.push(apiRuleDefaults[key]);
        }
      });
      rules = rules.map((r) => {
        const metaForDay = apiRuleByDate[d.date]?.[r.key];
        const defaultMeta = apiRuleDefaults[r.key];
        const showMeta = metaForDay || defaultMeta;
        const status: ApiRuleStatus = metaForDay?.status || "not_executed";
        return {
          ...r,
          title: showMeta?.title || r.title,
          description: showMeta?.description || r.description,
          limit: showMeta?.limit ?? r.limit,
          notes: showMeta?.notes ?? r.notes,
          status,
        };
      });

      const rrKey = "require_rr_declared";
      if (!rules.some(r => r.key === rrKey)) {
        rules.push(apiRuleDefaults[rrKey] ?? {
          key: rrKey,
          title: `${t('RRDeclared')}`,
          pass: apiRuleByDate[d.date]?.[rrKey]?.status == "passed" ? true : false,
          status: apiRuleByDate[d.date]?.[rrKey]?.status,
        });
      }
      if (!rules.some(r => r.key === "session_allowed")) {
        rules.push(apiRuleDefaults["session_allowed"] ?? {
          key: "session_allowed",
          title: `${t('Sessions')}`,
          pass: apiRuleByDate[d.date]?.[rrKey]?.status == "passed" ? true : false,
          status: apiRuleByDate[d.date]?.[rrKey]?.status,
        });
      }
      if (!rules.some(r => r.key === "pre_trade_journal")) {
        rules.push(apiRuleDefaults["pre_trade_journal"] ?? {
          key: "pre_trade_journal",
          title: `${t('PreTradeJournal')}`,
          pass: apiRuleByDate[d.date]?.[rrKey]?.status == "passed" ? true : false,
          status: apiRuleByDate[d.date]?.[rrKey]?.status,
        });
      }
      if (!rules.some(r => r.key === "require_first_trade_goal")) {
        rules.push(apiRuleDefaults["require_first_trade_goal"] ?? {
          key: "require_first_trade_goal",
          title: `${t('FirstTradeGoal')}`,
          pass: apiRuleByDate[d.date]?.[rrKey]?.status == "passed" ? true : false,
          status: apiRuleByDate[d.date]?.[rrKey]?.status,
        });
      }
      if (!rules.some(r => r.key === "require_stop_loss")) {
        rules.push(apiRuleDefaults["require_stop_loss"] ?? {
          key: "require_stop_loss",
          title: `${t('SLRequired')}`,
          pass: apiRuleByDate[d.date]?.[rrKey]?.status == "passed" ? true : false,
          status: apiRuleByDate[d.date]?.[rrKey]?.status,
        });
      }
      if (!rules.some(r => r.key === "max_sl_tp_change_percent")) {
        rules.push(apiRuleDefaults["max_sl_tp_change_percent"] ?? {
          key: "max_sl_tp_change_percent",
          title: `${t('MaxSLTPChange')}`,
          pass: apiRuleByDate[d.date]?.[rrKey]?.status == "passed" ? true : false,
          status: apiRuleByDate[d.date]?.[rrKey]?.status,
        });
      }

      rules = rules.map(r =>
        r.key === rrKey
          ? { ...r, status: (apiRuleByDate[d.date]?.[rrKey]?.status as ApiRuleStatus) || "not_executed" }
          : r
      );

      return { ...d, rules };
    });
  }, [list, apiRuleDefaults, apiRuleByDate, settings.minRRAllowed]);

  const currentDay = useMemo(() =>
    listWithApiStatus.find((d) => d.date === focusDate) || null,
    [listWithApiStatus, focusDate]
  );


  return (
    <div id="printArea" ref={printRef} className="mx-auto max-w-6xl px-1 space-y-4 max-w-full">
      {/* HEADER */}
      <header className="rounded-2xl border border-gray-200 bg-gray-50 p-4 shadow-sm header-dark">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm">
              {t("Trader")}: <span className="font-medium">{fullName}</span> | Date: {today}
            </p>
          </div>
          <div className="flex items-center gap-3 whitespace-nowrap action-button">
            <Badge tone="neutral">
              {t("ComplianceToday")}: {(daily.compliance * 100).toFixed(0)}%
            </Badge>
            <button
              onClick={handleDownloadPdf}
              style={{
                opacity: isDown ? "0.3" : "1",
                pointerEvents: isDown ? "none" : "all",
                cursor: isDown ? "not-allowed" : "pointer",
              }}
              className="flex items-center gap-1 gap-2 px-4 py-2 bg-[#E9B460] hover:bg-[#d1a154] text-white !rounded-lg transition-colors duration-200"
            >
              <Download className="w-4 h-4" />
              <span className="inline">Export PDF</span>
              <span className="hidden">PDF</span>
            </button>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 header-stat">
          <HeaderStat label={t("Passratedays", { days: "(7D)" })}>
            <div className="flex items-end gap-3">
              <span className="text-emerald-600 font-medium">{passRate}%</span>
              <Sparkline data={passRate7} height={36} />
            </div>
          </HeaderStat>
          <HeaderStat label={t("Tradestoday")}>
            <span className="text-gray-800 font-medium">{currentDay?.tradesCount || 0}</span>
          </HeaderStat>
          <div
            className="header-stat-item rounded-xl border border-gray-200 bg-white p-3 cursor-pointer hover:shadow-sm transition-shadow"
            onClick={() => setShowPersonalInfo((prev) => !prev)}
          >
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
              {t('BehaviourSection1Title')}
            </div>
            <div className="flex justify-between items-center gap-2 w-full">
              <span className="text-gray-800 font-medium">
                {(personalNumbers.personalYearNumber !== null || personalNumbers.personalMonthNumber !== null || personalNumbers.personalDayNumber !== null)
                  ? `DAI ${personalNumbers.personalDayNumber ?? '-'} • MRI ${personalNumbers.personalMonthNumber ?? '-'} • AMI ${personalNumbers.personalYearNumber ?? '-'}`
                  : "N/A"}
              </span>
              <span className="ml-2 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-chevron-right w-4 h-4 text-gray-400"
                >
                  <path d="m9 18 6-6-6-6"></path>
                </svg>
              </span>
            </div>
          </div>
          <Modal
            open={showPersonalInfo}
            title={t('BehaviourSection1Title')}
            onCancel={() => setShowPersonalInfo(false)}
            footer={null}
          >
            <div className="space-y-4 overflow-y-auto max-h-[70vh]" style={{ scrollbarWidth: "none" }}>
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">DAI</h3>
                  <span className="text-2xl font-bold text-blue-600">
                    {personalNumbers.personalDayNumber ?? "-"}
                  </span>
                </div>
                <p className="text-gray-600">{personalNumberMessage.day}</p>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">MRI</h3>
                  <span className="text-2xl font-bold text-blue-600">
                    {personalNumbers.personalMonthNumber ?? "-"}
                  </span>
                </div>
                <p className="text-gray-600">{personalNumberMessage.month}</p>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">AMI</h3>
                  <span className="text-2xl font-bold text-blue-600">
                    {personalNumbers.personalYearNumber ?? "-"}
                  </span>
                </div>
                <p className="text-gray-600">{personalNumberMessage.year}</p>
              </div>
            </div>
          </Modal>
        </div>
      </header>

      {/* Utilities Buttons - Enhanced for better UX */}

      <div className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-4xl">
            <Link
              to={`/${tradePlatform}/behavioral-performance/${id}`}
              className="group flex items-center justify-start bg-white hover:bg-gray-50 border border-gray-200 rounded-lg p-4 transition-all duration-200 hover:shadow-md hover:border-blue-300 no-underline max-h-[60px]"
            >
              <div className="flex items-center justify-center w-8 h-8 transition-colors duration-200 flex-shrink-0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-blue-600">
                  <g clipPath="url(#clip0_17_334)">
                    <path
                      d="M9.23676 12.6854L7.70405 7.23988C7.57944 6.80374 7.70405 6.34268 8.02804 6.0187L13.6978 0.348915C14.1713 -0.124606 14.9938 -0.124606 15.4548 0.348915L23.3302 8.2243C23.567 8.46106 23.6916 8.77259 23.6916 9.10904C23.6916 9.43303 23.5545 9.75702 23.3302 9.99378L17.6604 15.6636C17.4237 15.9003 17.0997 16.0249 16.7757 16.0249C16.6636 16.0249 16.5514 16.0125 16.4393 15.9751L11.0062 14.4424L7.00623 18.4424L8.3271 19.7632L6.57009 21.5203L5.23676 20.1994L4.52648 20.9097L5.84735 22.2305L4.09034 23.9875L1 20.9221L9.23676 12.6854ZM16.4143 13.3832L20.6885 9.10904L14.5826 2.99066L10.2959 7.2648L11.6417 12.0249L16.4143 13.3832Z"
                      fill="currentColor"
                    />
                    <path
                      d="M15.3551 4.99689L18.6822 8.32399C19.0435 8.68536 19.0435 9.28349 18.6822 9.64486C18.4952 9.83178 18.2585 9.91901 18.0217 9.91901C17.785 9.91901 17.5482 9.83178 17.3613 9.64486L14.0342 6.31776C13.6728 5.95639 13.6728 5.35826 14.0342 4.99689C14.3956 4.63552 14.9937 4.62306 15.3551 4.99689Z"
                      fill="currentColor"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_17_334"><rect width="24" height="24" fill="currentColor"></rect></clipPath>
                  </defs>
                </svg>
              </div>
              <span className="ml-2 text-sm md:!text-[15px] !no-underline font-medium text-gray-700 group-hover:text-blue-600 transition-colors duration-200">
                {t('BehavioralPerformance')}
              </span>
            </Link>

            <a
              href={"/" + tradePlatform + "/account-metrix/" + id}
              className="group flex items-center justify-start bg-white hover:bg-gray-50 border border-gray-200 rounded-lg p-4 transition-all duration-200 hover:shadow-md hover:border-green-300 no-underline max-h-[60px]"
            >
              <div className="flex items-center justify-center w-8 h-8 transition-colors duration-200 flex-shrink-0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-green-600">
                  <g clipPath="url(#clip0_6_2798)">
                    <path d="M2.59566 21.4054V0H0V24H24V21.4054H2.59566Z" fill="currentColor" />
                    <path
                      d="M4.94773 11.8576H6.86175C7.22307 11.8576 7.56485 11.6526 7.73087 11.3305L10.4945 6.01051C10.5628 5.88361 10.7484 5.89337 10.7679 5.89337C10.7972 5.89337 10.9534 5.91289 10.9925 6.01051L14.8596 15.8988C15.1721 16.709 15.9631 17.2361 16.881 17.2556C16.9006 17.2556 16.9103 17.2556 16.9299 17.2556C17.8283 17.2556 18.6193 16.7578 18.9611 15.9769L21.2852 10.7057H23.0235C23.5606 10.7057 24 10.2665 24 9.72959C24 9.19272 23.5606 8.75345 23.0235 8.75345H20.6505C20.2599 8.75345 19.9083 8.97796 19.7521 9.33914L17.174 15.1862C17.1252 15.3033 16.9592 15.3131 16.9201 15.3131C16.8615 15.3131 16.7248 15.3033 16.676 15.196L12.8089 5.29792C12.4964 4.50725 11.7542 3.9899 10.8656 3.94109C9.96714 3.89229 9.16638 4.34131 8.766 5.1027L6.27582 9.9053H4.94773C4.41063 9.9053 3.97119 10.3446 3.97119 10.8814C3.97119 11.4183 4.41063 11.8576 4.94773 11.8576Z"
                      fill="currentColor"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_6_2798"><rect width="24" height="24" fill="currentColor"></rect></clipPath>
                  </defs>
                </svg>
              </div>
              <span className="ml-4 text-sm md:!text-[15px] !no-underline font-medium text-gray-700 group-hover:text-green-600 transition-colors duration-200">
                {t("MetriX")}
              </span>
            </a>

            <a
              href={"/" + tradePlatform + "/account-live-metrix/" + id}
              className="group flex items-center justify-start bg-white hover:bg-gray-50 border border-gray-200 rounded-lg p-4 transition-all duration-200 hover:shadow-md hover:border-purple-300 no-underline max-h-[60px]"
            >
              <div className="flex items-center justify-center w-8 h-8 transition-colors duration-200 flex-shrink-0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-purple-600">
                  <g clipPath="url(#clip0_17_335)">
                    <path
                      d="M20.2205 2.31902H15.1056L14.1985 0.650307C13.9843 0.257669 13.5559 0 13.0898 0H10.9103C10.4441 0 10.0158 0.257669 9.78901 0.650307L8.89451 2.31902H3.77954C1.70079 2.31902 0 3.97546 0 6V20.319C0 22.3436 1.70079 24 3.77954 24H20.2205C22.2993 24 24.0001 22.3436 24.0001 20.319V6C24.0001 3.97546 22.2993 2.31902 20.2205 2.31902ZM21.4804 20.3313C21.4804 21.0061 20.9134 21.5583 20.2205 21.5583H3.77954C3.08662 21.5583 2.51969 21.0061 2.51969 20.3313V6C2.51969 5.32515 3.08662 4.77301 3.77954 4.77301H20.2205C20.9134 4.77301 21.4804 5.32515 21.4804 6V20.3313Z"
                      fill="currentColor"
                    />
                    <path d="M6.62686 10.1596H7.59695V15.313H9.19695V16.1719H6.62686V10.1596Z" fill="currentColor" />
                    <path d="M9.66309 10.1596H10.6332V16.1719H9.66309V10.1596Z" fill="currentColor" />
                    <path d="M12.7254 14.8222H12.7128L12.0829 10.1596H11.1002L12.0325 16.1719H13.3176L14.2624 10.1596H13.368L12.7254 14.8222Z" fill="currentColor" />
                    <path d="M17.3734 16.1719H14.7277V10.1596H17.3734V11.0185H15.6978V12.6014H17.0206V13.4603H15.6978V15.313H17.3734V16.1719Z" fill="currentColor" />
                  </g>
                  <defs>
                    <clipPath id="clip0_17_335"><rect width="24" height="24" fill="currentColor"></rect></clipPath>
                  </defs>
                </svg>
              </div>
              <span className="ml-4 text-sm md:!text-[15px] !no-underline font-medium text-gray-700 group-hover:text-purple-600 transition-colors duration-200">
                {t("LiveMetrix")}
              </span>
            </a>
          </div>
        </div>

      {/* RULE SETTINGS */}
      <div className="rule-card bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#E9B460] bg-default-color rounded-lg flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-settings w-5 h-5 text-white"
                >
                  <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </div>
              <div>
                <h2 className="!text-lg font-semibold text-gray-900 mb-0">{t('RuleSettings')}</h2>
                <p className="text-xs sm:text-sm text-gray-600 block !mb-0">{t('RuleSettings_desc')}</p>
              </div>
            </div>
            <button
              onClick={() => setShowRuleSetting((prev) => !prev)}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-eye-off w-4 h-4"
              >
                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                <line x1="2" x2="22" y1="2" y2="22"></line>
              </svg>
              <span className="hidden sm:inline">{t("Hide")}</span>
            </button>
          </div>
        </div>
        {showRuleSetting && (
          <div className="p-4 sm:p-6">
            <RuleSettingsForm value={settings} onChange={setSettings} />
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleSaveSettings}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-[#E9B460] bg-default-color hover:bg-[#d1a154] text-white !rounded-lg transition-colors duration-200 font-medium text-sm sm:text-base"
              >
                Save
              </button>
              {showToast && (
                <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg animate-fade-in">
                  Settings saved successfully!
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* PRE-TRADE PLAN */}
      <div className="plan-card bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-default-color bg-[#E9B460] rounded-lg flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-book-open w-5 h-5 text-white"
                >
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                </svg>
              </div>
              <div className="">
                <div className="flex items-center gap-2 w-fit">
                  <h2 className="!text-lg font-semibold text-gray-900 !mb-0 w-fit overflow-x-clip whitespace-nowrap">{t('Pre-TradePlan')}</h2>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-help-circle w-4 h-4 text-gray-400"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <path d="M12 17h.01"></path>
                  </svg>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 block !mb-0">{t('Pre-TradePlan_desc')}</p>
              </div>
            </div>
            <button
              onClick={() => setShowPreTradePlan((prev) => !prev)}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-eye-off w-4 h-4"
              >
                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                <line x1="2" x2="22" y1="2" y2="22"></line>
              </svg>
              <span className="hidden sm:inline">Hide</span>
            </button>
          </div>
        </div>
        {showPreTradePlan && (
          <div className="p-4 sm:p-6">
            <PreTradePlanForm
              settings={settings}
              value={planForFocus}
              onSave={async (p) => {
                setPlans((prev) => ({ ...prev, [focusDate]: p }));
                await fetchApiStatuses(startISO, endISO);
                const newList = await loadChecklistRange(trader, startISO, endISO, settings, apiRuleByDate, apiRuleDefaults, tradesCountByDate);
                setList(newList);
              }}
            />
            {preTradePlan && preTradePlan.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-[#E9B460]"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14,2 14,8 20,8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10,9 9,9 8,9"></polyline>
                    </svg>
                    {t('Pre-TradePlanHistory')}
                  </h4>
                  {preTradePlan.length > 0 && (
                    <button
                      onClick={() => setIsDialogOpen(true)}
                      className="text-xs text-[#E9B460] hover:text-[#d1a154] font-medium transition-colors"
                    >
                      {t('ViewAll')} ({preTradePlan.length})
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {preTradePlan.slice(0, 4).map((item: any) => (
                    <div
                      key={item.id}
                      className="bg-gray-50 rounded-lg p-3 border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => {
                        setSelectedEntry(item);
                        setIsDetailDialogOpen(true);
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-600">
                            {formatTo12Hour(item.condition.submittedAt || item.created_at)}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs font-medium text-[#E9B460]">
                            RR ≥ {item.condition.rrTarget || item.condition.rr || 'N/A'}
                          </span>
                        </div>
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                          {item.condition.mood}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 mb-1">
                        <span className="font-medium">{t("Symbol")}:</span> {item.condition.symbol || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-600 mb-1">
                        <span>{t("Notes")}:</span>
                        {Array.isArray(item.condition.notes) && item.condition.notes.filter((note: { active: boolean }) => note.active).length > 0 ? (
                          <ul className="mb-0 !pl-5">
                            {item.condition.notes
                              .filter((note: { active: boolean }) => note.active)
                              .map((note: { content: string; active: boolean }, index: number) => (
                                <li key={index} className="whitespace-nowrap">
                                  • {note.content}
                                </li>
                              ))}
                          </ul>
                        ) : (
                          <p>{t("No notes available")}</p>
                        )}
                      </div>
                      {item.condition.plannedWindows && item.condition.plannedWindows.length > 0 && (
                        <div className="text-xs text-gray-600">
                          <span className="font-medium">{t("Windows")}:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.condition.plannedWindows.map((window: any, index: number) => (
                              <span key={index} className="px-2 py-1 bg-white rounded border text-gray-700">
                                {window.label} {window.start}-{window.end}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PRE-ENTRY JOURNAL */}
      <div className="journal-card bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-default-color bg-[#E9B460] rounded-lg flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-pen-line w-5 h-5 text-white"
              >
                <path d="M12 20h9"></path>
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2 w-fit">
                <h2 className="!text-lg font-semibold text-gray-900 !mb-0 w-fit overflow-x-clip whitespace-nowrap">{t('Pre-EntryJournal')}</h2>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-help-circle w-4 h-4 text-gray-400"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <path d="M12 17h.01"></path>
                </svg>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600 block !mb-0">
                  {t('Pre-EntryJournal_desc')}
                </p>
              </div>
            </div>
          </div>
          {showPreEntryJournal && (
            <div className="">
              <PreEntryJournalForm
                dateISO={focusDate}
                equity={latestEquity}
                settings={settings}
                initial={preEntry && preEntry.length > 0 ? { condition: preEntry[0] } : undefined}
                onSave={async (e) => {
                  saveJournalEntry(e);
                }}
              />
              {preEntry.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-[#E9B460]"
                      >
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
                      </svg>
                      {t("JournalHistory")}
                    </h4>
                    {preEntry.length > 4 && (
                      <button
                        onClick={() => setIsDialogOpen(true)}
                        className="text-xs text-[#E9B460] hover:text-[#d1a154] font-medium transition-colors"
                      >
                        {t('ViewAll')} ({preEntry.length})
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {preEntry.slice(0, 4).map((j) => (
                      <div
                        key={j.id}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => {
                          setSelectedEntry(j);
                          setIsDetailDialogOpen(true);
                        }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-medium text-gray-600">
                                {new Date(j.createdAt).toLocaleTimeString()}
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 mb-1">
                              <span>{t("Conditions")}:</span>
                              {(Array.isArray(j.conditions) && j.conditions.length > 0) && (
                                <ul className="mb-0 !pl-5 text-gray-500">
                                  {j.conditions.map((condition: any, index: number) => (
                                    <li key={index} className="whitespace-nowrap">
                                      {condition.active && '• ' + condition?.content}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">{t("Entry")}:</span>
                                  <span className="font-mono font-medium">{j.entry}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">SL:</span>
                                  <span className="font-mono font-medium">{j.sl}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">{t("Lots")}:</span>
                                  <span className="font-mono font-medium">{j.lots}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">RR:</span>
                                  <span className="font-mono font-medium">{j.rr}</span>
                                </div>
                              </div>
                              <div className="space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">{t("Risk")}:</span>
                                  <span className="font-mono font-medium">{(j.riskCash ?? 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">{t("Risk")}%:</span>
                                  <span className="font-mono font-medium">{(j.riskPct ?? 0).toFixed(2)}%</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">TP P/L:</span>
                                  <span className="font-mono font-medium text-green-600">{(j.profitAtTP ?? 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">SL P/L:</span>
                                  <span className="font-mono font-medium text-red-600">{(j.lossAtSL ?? 0).toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeJournalEntry(j.id);
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-red-600 hover:bg-red-50 rounded transition-colors text-xs"
                          >
                            <Trash2 className="w-3 h-3" />
                            {t("Delete")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Modal
        open={isDialogOpen}
        title={
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('AllJournalEntries')}</h2>
            <button
              onClick={() => setIsDialogOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        }
        width={750}
        footer={null}
        onCancel={() => setIsDialogOpen(false)}
      >
        <div className="p-6">
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {preEntry.map((j) => (
              <div
                key={j.id}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setSelectedEntry(j);
                  setIsDetailDialogOpen(true);
                }}
              >
                <div className="flex items-start justify-between gap-3">

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {new Date(j.createdAt).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-500">{t("Entry")}:</span>
                          <span className="font-mono font-medium">{j.entry}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">SL:</span>
                          <span className="font-mono font-medium">{j.sl}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">{t("Lots")}:</span>
                          <span className="font-mono font-medium">{j.lots}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">RR:</span>
                          <span className="font-mono font-medium">{j.rr}</span>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-500">{t("Risk")}:</span>
                          <span className="font-mono font-medium">{(j.riskCash ?? 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">{t("Risk")}%:</span>
                          <span className="font-mono font-medium">{(j.riskPct ?? 0).toFixed(2)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">TP P/L:</span>
                          <span className="font-mono font-medium text-green-600">{(j.profitAtTP ?? 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">SL P/L:</span>
                          <span className="font-mono font-medium text-red-600">{(j.lossAtSL ?? 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeJournalEntry(j.id);
                    }}
                    className="flex items-center gap-1 px-3 py-1 text-red-600 hover:bg-red-50 rounded transition-colors text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t("Delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
      <Modal
        open={isDetailDialogOpen}
        onCancel={() => {
          setIsDetailDialogOpen(!isDetailDialogOpen)
        }}
        width={750}
        footer={null}
        title={
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <FileText size={20} className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                {(selectedEntry as any)?.condition ? t('Pre-TradePlanDetails') : t('JournalEntryDetails')}
              </h3>
            </div>
          </div>
        }
      >
        <div className="bg-white rounded-lg max-w-4xl max-h-[70vh] overflow-y-auto p-6"
          style={{
            scrollbarWidth: 'none'
          }}
        >
          {/* Header */}


          {/* Content */}
          {selectedEntry && (
            <div className="space-y-6">
              {(selectedEntry as any).condition ? (
                // Pre-Trade Plan Details
                <div className="space-y-6">
                  {/* Main Info Section */}
                  <div className="bg-sub-container rounded-xl p-6 border border-blue-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <Info size={16} className="text-blue-600" />
                      {t('BasicInformation')}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoRow
                        icon={<Calendar size={16} className="text-gray-500" />}
                        label={t('CreatedAt')}
                        value={formatTo12Hour((selectedEntry as any).condition.submittedAt || (selectedEntry as any).created_at)}
                      />
                      <InfoRow
                        icon={<Smile size={16} className="text-blue-500" />}
                        label={t('Mood')}
                        value={
                          <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-medium">
                            {(selectedEntry as any).condition.mood}
                          </span>
                        }
                      />
                      <InfoRow
                        icon={<TrendingUp size={16} className="text-purple-500" />}
                        label={t('Symbol')}
                        value={(selectedEntry as any).condition.symbol || 'N/A'}
                        valueClassName="font-bold text-purple-700"
                      />
                      <InfoRow
                        icon={<Target size={16} className="text-amber-500" />}
                        label={t('RRTarget')}
                        value={`≥ ${(selectedEntry as any).condition.rrTarget || (selectedEntry as any).condition.rr || 'N/A'}`}
                        valueClassName="font-bold text-[#E9B460]"
                      />
                    </div>
                  </div>

                  {/* Price Expectations */}
                  {((selectedEntry as any).condition.expectedHighPrice || (selectedEntry as any).condition.expectedLowPrice) && (
                    <div className="bg-sub-container rounded-xl p-6 border border-green-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <BarChart3 size={16} className="text-green-600" />
                        {t('PriceExpectations')}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(selectedEntry as any).condition.expectedHighPrice && (
                          <InfoRow
                            icon={<ArrowUp size={16} className="text-green-600" />}
                            label={t('ExpectedHighPrice')}
                            value={(selectedEntry as any).condition.expectedHighPrice}
                            valueClassName="text-green-700 font-bold"
                          />
                        )}
                        {(selectedEntry as any).condition.expectedLowPrice && (
                          <InfoRow
                            icon={<ArrowDown size={16} className="text-red-600" />}
                            label={t('ExpectedLowPrice')}
                            value={(selectedEntry as any).condition.expectedLowPrice}
                            valueClassName="text-red-700 font-bold"
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Trading Windows */}
                  {(selectedEntry as any).condition.plannedWindows && (selectedEntry as any).condition.plannedWindows.length > 0 && (
                    <div className="bg-sub-container rounded-xl p-6 border border-amber-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <Clock size={16} className="text-amber-600" />
                        {t('TradingWindows')}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(selectedEntry as any).condition.plannedWindows.map((window: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-white p-4 rounded-lg border border-amber-200 shadow-sm hover:shadow-md transition-shadow"
                          >
                            <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                              {window.label}
                            </span>
                            <span className="text-sm font-bold text-gray-900 font-mono bg-amber-100 px-3 py-1 rounded">
                              {window.start} - {window.end}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Journal Entry Details
                <div className="space-y-6">
                  {/* Entry Details Section */}
                  <div className="bg-sub-container rounded-xl p-6 border border-purple-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <Layers size={16} className="text-purple-600" />
                      {t('EntryDetails')}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoRow
                        icon={<Calendar size={16} className="text-gray-500" />}
                        label={t(' ')}
                        value={new Date(selectedEntry.createdAt).toLocaleString()}
                      />
                      <InfoRow
                        icon={<ArrowRightCircle size={16} className="text-blue-500" />}
                        label={t("Entry")}
                        value={selectedEntry.entry}
                        valueClassName="font-bold text-blue-700"
                      />
                      <InfoRow
                        icon={<Shield size={16} className="text-red-500" />}
                        label={t('StopLoss')}
                        value={selectedEntry.sl}
                        valueClassName="font-bold text-red-600"
                      />
                      <InfoRow
                        icon={<Package size={16} className="text-indigo-500" />}
                        label={t("Lots")}
                        value={selectedEntry.lots}
                        valueClassName="font-bold text-indigo-700"
                      />
                      <InfoRow
                        icon={<TrendingUp size={16} className="text-amber-500" />}
                        label={t("RRRatio")}
                        value={selectedEntry.rr}
                        valueClassName="font-bold text-[#E9B460]"
                      />
                      <InfoRow
                        icon={<DollarSign size={16} className="text-gray-500" />}
                        label={t('ValuePerPoint')}
                        value={selectedEntry.valuePerPoint}
                      />
                    </div>
                  </div>

                  {/* Risk & Profit Section */}
                  <div className="bg-sub-container rounded-xl p-6 border border-slate-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <Activity size={16} className="text-slate-600" />
                      {t('RiskAndProfit')}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoRow
                        icon={<AlertTriangle size={16} className="text-orange-500" />}
                        label={t('Risk_Cash')}
                        value={(selectedEntry.riskCash ?? 0).toFixed(2)}
                        valueClassName="font-bold text-orange-600"
                      />
                      <InfoRow
                        icon={<Percent size={16} className="text-orange-500" />}
                        label={`${t("Risk")} (%)`}
                        value={`${(selectedEntry.riskPct ?? 0).toFixed(2)}%`}
                        valueClassName="font-bold text-orange-600"
                      />
                      <InfoRow
                        icon={<TrendingUp size={16} className="text-green-500" />}
                        label="TP P/L"
                        value={(selectedEntry.profitAtTP ?? 0).toFixed(2)}
                        valueClassName="font-bold text-green-600"
                      />
                      <InfoRow
                        icon={<TrendingDown size={16} className="text-red-500" />}
                        label="SL P/L"
                        value={(selectedEntry.lossAtSL ?? 0).toFixed(2)}
                        valueClassName="font-bold text-red-600"
                      />
                      <InfoRow
                        icon={<Wallet size={16} className="text-blue-500" />}
                        label={t('EquityatEntry')}
                        value={selectedEntry.equityAtEntry ?? "N/A"}
                        valueClassName="font-bold text-blue-700"
                      />
                    </div>
                  </div>

                  {/* Conditions Section */}
                  {(selectedEntry?.conditions && Array.isArray(selectedEntry?.conditions) && selectedEntry?.conditions?.length > 0) && (
                    <div className="bg-sub-container rounded-xl p-6 border border-cyan-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <CheckCircle size={16} className="text-cyan-600" />
                        {t('Conditions')}
                      </h4>
                      <div className="space-y-2">
                        {selectedEntry?.conditions?.map((condition: any, index: number) => (
                          condition.active && (
                            <div
                              key={index}
                              className="flex items-start gap-3 bg-white p-3 rounded-lg border border-cyan-200"
                            >
                              <div className="mt-1">
                                <CheckCircle size={16} className="text-cyan-600" />
                              </div>
                              <span className="text-sm text-gray-700 flex-1">
                                {condition?.content}
                              </span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
      {/* CURRENT DAY */}
      {/* <div className="space-y-3">
          {settings.requireFirstTradeGoal && !planForFocus && (
            <Card>
              <CardContent>
                <div className="text-sm text-amber-700">
                  ⚠️ You haven't declared RR target/plan for{" "}
                  {fmt(new Date(focusDate + "T00:00:00Z"))}. Complete the form
                  above before your first trade.
                </div>
              </CardContent>
            </Card>
          )}
          </div> */}
      {/* OVERVIEW TABLE */}
      {/* Controls */}
      <div className="controls-card bg-white rounded-xl shadow-sm border border-gray-200">

        <div className="rounded-xl border border-gray-200">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-default-color bg-[#E9B460] rounded-lg flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
                    className="lucide lucide-user w-5 h-5 text-white"
                  >
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
                    className="lucide lucide-calendar w-4 h-4 text-gray-500"
                  >
                    <path d="M8 2v4"></path>
                    <path d="M16 2v4"></path>
                    <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                    <path d="M3 10h18"></path>
                  </svg>
                  <span className="text-gray-600">{t('Focusdate')}: {focusDate}</span>
                </div>
                <select
                  value={rangeDays}
                  onChange={(e) =>
                    setRangeDays(parseInt((e.target as HTMLSelectElement).value))
                  }
                  className="px-2 py-1 h-10 border border-gray-300 rounded-lg !text-sm"
                >
                  <option value={7}>{t("LastNDays", { days: 7 })}</option>
                  <option value={14}>{t("LastNDays", { days: 14 })}</option>
                  <option value={30}>{t("LastNDays", { days: 30 })}</option>
                  <option value={60}>{t("LastNDays", { days: 60 })}</option>
                </select>
              </div>
            </div>
          </div>
          <div className="px-4 sm:px-6 sm:pt-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <div className="text-xs sm:text-sm text-gray-600">
                {loading
                  ? "Loading…"
                  : `${summary.days} days, avg pass rate ${Math.round(
                    summary.passRate * 100
                  )}%`}
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => setFocusDate(addDays(focusDate, -1))}
                  className="p-2 border border-gray-300 !h-10 !rounded-lg hover:bg-gray-50 flex items-center gap-1 flex-row shadow-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
                    className="lucide lucide-chevron-left w-4 h-4"
                  >
                    <path d="m15 18-6-6 6-6"></path>
                  </svg>
                  <span className="text-sm font-medium  inline">{t("Prev")}</span>
                </button>

                <button
                  onClick={() => setFocusDate(addDays(focusDate, +1))}
                  className="p-2 border border-gray-300 !h-10 !rounded-lg hover:bg-gray-50 flex items-center gap-1 flex-row shadow-sm"
                >
                  <span className="text-sm font-medium  inline">{t("Next")}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
                    className="lucide lucide-chevron-right w-4 h-4"
                  >
                    <path d="m9 18 6-6-6-6"></path>
                  </svg>
                </button>
                <button
                  onClick={() => setFocusDate(ymd(new Date()))}
                  className="p-2 border border-gray-300 !h-10 !rounded-lg hover:bg-blue-700 flex items-center gap-1 flex-row shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  <span className="text-sm font-medium inline">{t("Today")}</span>
                </button>
              </div>
            </div>
          </div>
          {currentDay ? (
            <div className="px-4 sm:px-6 sm:pt-4 border-b border-gray-200 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <h3 className="!text-base  font-semibold text-gray-900 mb-4">
                    {t("Compliance")}
                  </h3>
                  <div className="space-y-4">
                    {(() => {
                      const { pct, passed, total } = calcStatusPassRate(
                        currentDay.rules
                      );
                      return (
                        <>
                          <div
                            className={`flex items-center justify-between p-3 ${pct > 50 ? "bg-green-50 " : "bg-red-50"
                              } rounded-lg`}
                          >
                            <span
                              className={`text-xl sm:text-2xl font-bold ${pct > 50 ? "text-green-800 " : "text-red-800"
                                } `}
                            >
                              {pct}%
                            </span>
                            <span className="text-sm font-medium">
                              {passed}/{total} {t('rulespassed')}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <h3 className="!text-base font-semibold text-gray-900 mb-4">
                    {t("Score")}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-xs sm:text-sm font-medium">
                        {t("Trades")}
                      </span>
                      <span className="text-base sm:text-lg font-bold text-blue-800">
                        {currentDay.tradesCount || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 pb-6">
                {currentDay.rules.map((rc) => (
                  <div
                    key={rc.key}
                    className={`flex items-start justify-between gap-3 py-2 !rounded-md  px-3
                  ${rc.status === "passed"
                        ? "bg-green-50"
                        : rc.status === "failed"
                          ? "bg-rose-50"
                          : "bg-gray-50"
                      }
                    `}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 whitespace-nowrap">
                        {t(`${rc.title.toLowerCase().replace(/[ \/-]/g, '_')}`)}
                      </div>
                      {rc.notes && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {rc.notes}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {rc.value != null && (
                        <span className="text-xs text-gray-600">
                          {String(rc.value)}
                          {rc.limit ? ` / ${rc.limit}` : ``}
                        </span>
                      )}
                      {/* Custom status pill */}
                      <span
                        className={
                          rc.status === "passed"
                            ? "px-2 py-1 whitespace-nowrap bg-green-100 text-green-800 text-xs sm:text-xs font-medium rounded-full"
                            : rc.status === "failed"
                              ? "px-2 py-1 whitespace-nowrap bg-rose-100 text-rose-800 text-xs sm:text-xs font-medium rounded-full"
                              : "px-2 py-1 whitespace-nowrap bg-gray-100 text-gray-700 text-xs sm:text-xs font-medium rounded-full"
                        }
                      >
                        {rc.status === "passed"
                          ? t("PASS")
                          : rc.status === "failed"
                            ? t("FAIL")
                            : t("NOTEXECUTED")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="px-4 sm:px-6 text-center text-gray-500">
              {t('Nodatafor')} {fmt(new Date(focusDate + "T00:00:00Z"))}
            </div>
          )}
        </div>
      </div>
      {/* Overview table (status text thay vì ✓/✗) */}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="font-medium">{t("Overview")}</div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap" style={{ minWidth: '1000px' }}>
              <thead>
                <tr className="text-center text-gray-500 bg-gray-50 !font-thin">
                  <th className="sticky left-0 z-30 bg-gray-50 px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap !border-r border-gray-100 shadow-sm">
                    {t("Date")}
                  </th>
                  <th className=" px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t("Trades")}
                  </th>
                  <th className=" px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('Passrate')}
                  </th>
                  {RULE_COLUMNS(t).map((col) => (
                    <th
                      key={col.key}
                      className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {listWithApiStatus.map((d) => {
                  const trades = d.tradesCount; // Sử dụng tradesCount đã được override với dữ liệu thực
                  const { pct } = calcStatusPassRate(d.rules);

                  const get = (k: string) => {
                    const rule = d.rules.find((x) => x.key === k);
                    if (k === 'max_sl_tp_change_percent') {
                    }
                    return rule;
                  };

                  return (
                    <tr
                      key={d.date}
                      className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setFocusDate(d.date)}
                    >
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 sticky left-0 z-20 bg-white !border-r border-gray-100 shadow-sm">
                        {fmt(new Date(d.date + "T00:00:00Z"))}
                      </td>
                      {/* Trades (mới) */}
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-center justify-center">
                        {trades}
                      </td>
                      {/* Pass rate (mới, theo status) */}
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-center justify-center">
                        {pct}%
                      </td>
                      {RULE_COLUMNS(t).map((col) => (
                        <td key={col.key} className="px-3 sm:px-6 py-4 whitespace-nowrap text-center justify-center">
                          {(() => {
                            const rc = get(col.key);
                            return (
                              <div className="flex items-center justify-center" title={rc?.notes || rc?.description || ""}>
                                {!rc && "-"}
                                {rc && rc.status === "passed" && (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="lucide lucide-check-circle w-4 h-4 !text-green-600"
                                  >
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                  </svg>

                                )}
                                {rc && rc.status === "failed" && (
                                  <span className="w-4 h-4 text-red-600 flex items-center justify-center">✗</span>
                                )}
                                {rc && rc.status !== "passed" && rc.status !== "failed" && "-"}
                              </div>
                            );
                          })()}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div >
  );
}
